import moment from "moment";
import {
  Row,
  Col,
  Card,
  Button,
  Container,
  ButtonGroup
} from "react-bootstrap";
import styled from "styled-components";
import { useSelector, useDispatch } from "react-redux";
import { useForm, FormProvider } from "react-hook-form";
import { useRef, useState, useEffect, useCallback } from "react";
import PDFViewer from "./pdf-viewer";
import Loader from "../loader/loader";
import {
  RADIOS,
  RADIO_RANGE,
  findRangeMode,
  DATE_RANGE_MODE,
  handleOnRangeChange,
  handleRadioOnSelect,
  handlePickerLeftButton,
  handlePickerRightButton
} from "../analytics/analytics-helper";
import {
  generateScriptUrl,
  formatLeadServiceInsightsForBarChart
} from "../../services/common-service";
import {
  updateLeadBlockTime,
  getAppointmentEmailTemplates,
  getLeadServiceGlobalInsights,
  updateAppointmentEmailTemplates,
  updateSystemCallingDownTimeWebhook,
  updateSystemCallingDownTimeInMinutes,
  updateOfferFollowupMaxNotReachedAttempts,
  updateAiLeadserviceLeadEnabled,
  updateAiLeadserviceLeadWebhook,
  updateAiLeadserviceLeadTrigger,
  updateAiLeadserviceEmployeeEnabled,
  updateAiLeadserviceEmployeeWebhook,
  updateAiLeadserviceEmployeeTrigger
} from "../../services/api-declaration";
import EditorModal from "./email-editor-modal";
import { setMe } from "../../store/slices/auth";
import UploadScriptDialog from "./upload-script-dialog";
import {
  APPOINTMENT_EMAIL_TEMPLATES,
  SCRIPT_LOCAL_FILE_NAME,
  SCRIPT_WORKSPACE_PATH
} from "../strings";
import { ACCESS_CONFIG, LS_TYPE_FILTERS, SCRIPT_ROUTE } from "../strings";
import LeadserviceOverview from "../funnel-leadservice-overview";
import DatePickerLimitRange from "../analytics/date-range-picker";
import LeadServiceInsightsBarChart from "../ls-insights-bar-chart";
import { showError, showSuccess } from "../../services/toast-service";
import { CustomCard, PageHeading, ToggleBtn } from "../common-styling";
import LeadServiceTypesFilter from "../analytics/lead-service-types-filter";
import LeadServicePanelWorkspaceConfigField from "./ls-panel-workspace-config-field";
import LeadServicePanelDailyReportWebhook from "./ls-panel-daily-report-webhook";
import { hasAccess } from "../../services/access";
import { useTranslation } from "react-i18next";

const PlusSpan = styled.span`
  font-size: 20px;
`;
const EmailPreview = styled.div`
  height: 115px;
  overflow-y: auto;
  white-space: pre-wrap;
`;
const SubjectPreview = styled.div`
  height: 20px;
  white-space: pre-wrap;
`;
const HorizontalLine = styled.hr`
  margin-bottom: 0;
  margin-right: 0.5rem !important;
  margin-left: 0.5rem !important;
  color: #959ba1;
`;

const { CONFIRM_EMAIL, CANCEL_EMAIL, POSTPONE_EMAIL, LEAD_NOT_REACHED } =
  APPOINTMENT_EMAIL_TEMPLATES;

const Leadservice = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingInsights, setIsLoadingInsights] = useState(true);
  const [loadingLeadBlock, setLoadingLeadBlock] = useState(false);
  const [loadingDownTime, setLoadingDownTime] = useState(false);
  const [loadingDownTimeWebhook, setLoadingDownTimeWebhook] = useState(false);
  const [loadingOfferFollowupAttempts, setLoadingOfferFollowupAttempts] =
    useState(false);
  const [availableAfter, setAvailableAfter] = useState();
  const [
    offerFollowupMaxNotReachedAttempts,
    setOfferFollowupMaxNotReachedAttempts
  ] = useState(5);
  const [systemCallingDownTime, setSystemCallingDownTime] = useState();
  const [systemCallingDownTimeWebhook, setSystemCallingDownTimeWebhook] =
    useState("");
  // AI Leadservice - LEAD (Neukunden)
  const [aiLeadserviceLeadEnabled, setAiLeadserviceLeadEnabled] =
    useState(false);
  const [aiLeadserviceLeadWebhook, setAiLeadserviceLeadWebhook] = useState("");
  const [aiLeadserviceLeadTrigger, setAiLeadserviceLeadTrigger] = useState(5);
  const [loadingAiLeadEnabled, setLoadingAiLeadEnabled] = useState(false);
  const [loadingAiLeadWebhook, setLoadingAiLeadWebhook] = useState(false);
  const [loadingAiLeadTrigger, setLoadingAiLeadTrigger] = useState(false);

  // AI Leadservice - EMPLOYEE (Mitarbeitergewinnung)
  const [aiLeadserviceEmployeeEnabled, setAiLeadserviceEmployeeEnabled] =
    useState(false);
  const [aiLeadserviceEmployeeWebhook, setAiLeadserviceEmployeeWebhook] =
    useState("");
  const [aiLeadserviceEmployeeTrigger, setAiLeadserviceEmployeeTrigger] =
    useState(5);
  const [loadingAiEmployeeEnabled, setLoadingAiEmployeeEnabled] =
    useState(false);
  const [loadingAiEmployeeWebhook, setLoadingAiEmployeeWebhook] =
    useState(false);
  const [loadingAiEmployeeTrigger, setLoadingAiEmployeeTrigger] =
    useState(false);

  const [showScriptDialog, setShowScriptDialog] = useState(false);
  const [scriptTitle, setScriptTitle] = useState("");
  const [modalType, setModalType] = useState(null);
  const [lsInisights, setLsInsights] = useState({});
  const [lsType, setLsType] = useState(LS_TYPE_FILTERS.ALL);
  const [radioValue, setRadioValue] = useState("1");
  const [dateRange, setDateRange] = useState([new Date(), new Date()]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const dateRangeModeRef = useRef(DATE_RANGE_MODE.ALL_TIME); // Current date range mode
  const controllerRef = useRef(null);

  const toggleUploadDialog = () => setShowScriptDialog(!showScriptDialog);

  const onEditClick = (title) => {
    toggleUploadDialog();
    setScriptTitle(title);
  };

  const fetchWorkspaceUserData = async () => {
    try {
      const resp = localStorage.getItem("userData");
      if (!resp) return;
      const workspace = JSON.parse(resp)?.workspace;
      if (workspace) {
        setAvailableAfter(workspace.availableAfterDelay);
        setOfferFollowupMaxNotReachedAttempts(
          workspace.offerFollowupMaxNotReachedAttempts || 5
        );
        setSystemCallingDownTime(workspace.systemCallingDownTime ?? 10);
        setSystemCallingDownTimeWebhook(
          workspace.systemCallingDownTimeWebhook || ""
        );

        // LEAD block
        setAiLeadserviceLeadEnabled(
          workspace.aiLeadservice?.lead?.enabled || false
        );
        setAiLeadserviceLeadWebhook(
          workspace.aiLeadservice?.lead?.webhookUrl || ""
        );
        setAiLeadserviceLeadTrigger(
          workspace.aiLeadservice?.lead?.contactAttemptsThreshold || 5
        );

        // EMPLOYEE block
        setAiLeadserviceEmployeeEnabled(
          workspace.aiLeadservice?.employee?.enabled || false
        );
        setAiLeadserviceEmployeeWebhook(
          workspace.aiLeadservice?.employee?.webhookUrl || ""
        );
        setAiLeadserviceEmployeeTrigger(
          workspace.aiLeadservice?.employee?.contactAttemptsThreshold || 5
        );
      }
    } catch (err) {
      console.error("Failed to fetch workspace data:", err);
    }
  };

  const afterUpdateSuccessfully = ({ successMsg, key, val }) => {
    dispatch(
      setMe({
        userData: {
          ...user,
          workspace: {
            ...user.workspace,
            [key]: val
          }
        }
      })
    );
    showSuccess(successMsg);
  };

  const updateLeadBlockTimeFunc = async () => {
    try {
      setLoadingLeadBlock(true);
      const resp = await updateLeadBlockTime({ availableAfter });
      if (resp.data.data) {
        afterUpdateSuccessfully({
          val: availableAfter,
          key: "availableAfterDelay",
          successMsg: resp.data.message
        });
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoadingLeadBlock(false);
    }
  };

  const updateSystemCallingDownTime = async () => {
    try {
      setLoadingDownTime(true);
      const resp = await updateSystemCallingDownTimeInMinutes({
        systemCallingDownTime
      });
      if (resp.data.data) {
        afterUpdateSuccessfully({
          val: systemCallingDownTime,
          key: "systemCallingDownTime",
          successMsg: resp.data.message
        });
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoadingDownTime(false);
    }
  };

  const updateOfferFollowupAttempts = async () => {
    try {
      setLoadingOfferFollowupAttempts(true);
      const resp = await updateOfferFollowupMaxNotReachedAttempts({
        maxNotReachedAttempts: offerFollowupMaxNotReachedAttempts
      });
      if (resp.data.data) {
        afterUpdateSuccessfully({
          val: Number(offerFollowupMaxNotReachedAttempts),
          key: "offerFollowupMaxNotReachedAttempts",
          successMsg: resp.data.message
        });
      }
    } catch (err) {
      console.log(err);
      showError(
        err?.response?.data?.message ||
          t("offerFollowupAgent.errors.saveOutcome")
      );
    } finally {
      setLoadingOfferFollowupAttempts(false);
    }
  };

  const updateCallingDownTimeWebhook = async () => {
    try {
      setLoadingDownTimeWebhook(true);
      const resp = await updateSystemCallingDownTimeWebhook({
        systemCallingDownTimeWebhook
      });
      if (resp.data.data) {
        afterUpdateSuccessfully({
          val: systemCallingDownTimeWebhook,
          key: "systemCallingDownTimeWebhook",
          successMsg: resp.data.message
        });
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoadingDownTimeWebhook(false);
    }
  };

  // === AI Leadservice LEAD Handlers ===
  const handleSaveAiLeadserviceLeadEnabled = async () => {
    setLoadingAiLeadEnabled(true);
    try {
      await updateAiLeadserviceLeadEnabled({
        enabled: aiLeadserviceLeadEnabled
      });
      dispatch(
        setMe({
          userData: {
            ...user,
            workspace: {
              ...user.workspace,
              aiLeadservice: {
                ...user.workspace.aiLeadservice,
                lead: {
                  ...user.workspace.aiLeadservice?.lead,
                  enabled: aiLeadserviceLeadEnabled
                }
              }
            }
          }
        })
      );
      showSuccess(t("aiLeadservice.lead.enabledSuccess"));
    } catch (error) {
      showError(t("aiLeadservice.lead.enabledError"));
    }
    setLoadingAiLeadEnabled(false);
  };

  const handleSaveAiLeadserviceLeadWebhook = async () => {
    setLoadingAiLeadWebhook(true);
    try {
      await updateAiLeadserviceLeadWebhook({
        webhook: aiLeadserviceLeadWebhook
      });
      dispatch(
        setMe({
          userData: {
            ...user,
            workspace: {
              ...user.workspace,
              aiLeadservice: {
                ...user.workspace.aiLeadservice,
                lead: {
                  ...user.workspace.aiLeadservice?.lead,
                  webhookUrl: aiLeadserviceLeadWebhook
                }
              }
            }
          }
        })
      );
      showSuccess(t("aiLeadservice.lead.webhookSuccess"));
    } catch (error) {
      showError(t("aiLeadservice.lead.webhookError"));
    }
    setLoadingAiLeadWebhook(false);
  };

  const handleSaveAiLeadserviceLeadTrigger = async () => {
    setLoadingAiLeadTrigger(true);
    try {
      await updateAiLeadserviceLeadTrigger({
        trigger: aiLeadserviceLeadTrigger
      });
      dispatch(
        setMe({
          userData: {
            ...user,
            workspace: {
              ...user.workspace,
              aiLeadservice: {
                ...user.workspace.aiLeadservice,
                lead: {
                  ...user.workspace.aiLeadservice?.lead,
                  contactAttemptsThreshold: aiLeadserviceLeadTrigger
                }
              }
            }
          }
        })
      );
      showSuccess(t("aiLeadservice.lead.triggerSuccess"));
    } catch (error) {
      showError(t("aiLeadservice.lead.triggerError"));
    }
    setLoadingAiLeadTrigger(false);
  };

  // === AI Leadservice EMPLOYEE Handlers ===
  const handleSaveAiLeadserviceEmployeeEnabled = async () => {
    setLoadingAiEmployeeEnabled(true);
    try {
      await updateAiLeadserviceEmployeeEnabled({
        enabled: aiLeadserviceEmployeeEnabled
      });
      dispatch(
        setMe({
          userData: {
            ...user,
            workspace: {
              ...user.workspace,
              aiLeadservice: {
                ...user.workspace.aiLeadservice,
                employee: {
                  ...user.workspace.aiLeadservice?.employee,
                  enabled: aiLeadserviceEmployeeEnabled
                }
              }
            }
          }
        })
      );
      showSuccess(t("aiLeadservice.employee.enabledSuccess"));
    } catch (error) {
      showError(t("aiLeadservice.employee.enabledError"));
    }
    setLoadingAiEmployeeEnabled(false);
  };

  const handleSaveAiLeadserviceEmployeeWebhook = async () => {
    setLoadingAiEmployeeWebhook(true);
    try {
      await updateAiLeadserviceEmployeeWebhook({
        webhook: aiLeadserviceEmployeeWebhook
      });
      dispatch(
        setMe({
          userData: {
            ...user,
            workspace: {
              ...user.workspace,
              aiLeadservice: {
                ...user.workspace.aiLeadservice,
                employee: {
                  ...user.workspace.aiLeadservice?.employee,
                  webhookUrl: aiLeadserviceEmployeeWebhook
                }
              }
            }
          }
        })
      );
      showSuccess(t("aiLeadservice.employee.webhookSuccess"));
    } catch (error) {
      showError(t("aiLeadservice.employee.webhookError"));
    }
    setLoadingAiEmployeeWebhook(false);
  };

  const handleSaveAiLeadserviceEmployeeTrigger = async () => {
    setLoadingAiEmployeeTrigger(true);
    try {
      await updateAiLeadserviceEmployeeTrigger({
        trigger: aiLeadserviceEmployeeTrigger
      });
      dispatch(
        setMe({
          userData: {
            ...user,
            workspace: {
              ...user.workspace,
              aiLeadservice: {
                ...user.workspace.aiLeadservice,
                employee: {
                  ...user.workspace.aiLeadservice?.employee,
                  contactAttemptsThreshold: aiLeadserviceEmployeeTrigger
                }
              }
            }
          }
        })
      );
      showSuccess(t("aiLeadservice.employee.triggerSuccess"));
    } catch (error) {
      showError(t("aiLeadservice.employee.triggerError"));
    }
    setLoadingAiEmployeeTrigger(false);
  };

  const methods = useForm({
    defaultValues: {
      CONFIRM_EMAIL: { subject: "", content: "" },
      CANCEL_EMAIL: { subject: "", content: "" },
      POSTPONE_EMAIL: { subject: "", content: "" },
      LEAD_NOT_REACHED: { subject: "", content: "" }
    }
  });

  const { setValue, getValues } = methods;

  const fetchEmailTemplates = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data } = await getAppointmentEmailTemplates();
      const templates = data.data;
      const templateMap = templates.reduce((acc, template) => {
        acc[template.type] = {
          subject: template.subject || "",
          content: template.template || ""
        };
        return acc;
      }, {});
      Object.keys(templateMap).forEach((type) => {
        setValue(type, templateMap[type]);
      });
    } catch (error) {
      showError(error.response?.data?.message);
    } finally {
      setIsLoading(false);
    }
  }, [setValue]);

  const updateEmailTemplate = async (type, { subject, template }) => {
    try {
      setIsLoading(true);
      const payload = { type, subject, template };
      const { data } = await updateAppointmentEmailTemplates(payload);
      showSuccess(data?.message);
    } catch (error) {
      showError(error.response?.data?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (type) => {
    setModalType(type);
  };
  const handleCloseModal = () => {
    setModalType(null);
  };

  const getTitle = (type) => {
    switch (type) {
      case CONFIRM_EMAIL:
        return t("emailEdit.confirm");
      case CANCEL_EMAIL:
        return t("emailEdit.cancel");
      case POSTPONE_EMAIL:
        return t("emailEdit.postpone");
      case LEAD_NOT_REACHED:
        return t("emailEdit.notReached");
      default:
        return "";
    }
  };

  const handleSaveContent = ({ subject, content }) => {
    if (modalType) {
      const currentValues = getValues(modalType) || {};
      setValue(modalType, { ...currentValues, subject, content });
      updateEmailTemplate(modalType, { subject, template: content });
    }
    handleCloseModal();
  };

  const handleRadioSelect = (value) => {
    dateRangeModeRef.current = RADIO_RANGE[value];
    const [startDate, endDate] = handleRadioOnSelect({
      radioValue: value,
      dateRangeMode: dateRangeModeRef.current,
      radioValueSetter: setRadioValue,
      dateRangeSetter: setDateRange,
      selectedDateSetter: setSelectedDate
    });
    if (RADIO_RANGE[value] !== DATE_RANGE_MODE.RANGE) {
      setDateRange([startDate, endDate]);
    }
  };

  const onRangeChange = (range) => {
    const [startDate, endDate] = handleOnRangeChange({
      range,
      dateRangeMode: dateRangeModeRef.current,
      dateRangeSetter: setDateRange,
      selectedDateSetter: setSelectedDate
    });
    if (dateRangeModeRef.current === DATE_RANGE_MODE.RANGE) {
      setDateRange([startDate, endDate]);
    }
  };

  const handleLeftBtn = () =>
    handlePickerLeftButton({
      startRange: dateRange[0],
      dateRangeMode: dateRangeModeRef.current,
      dateRangeSetter: setDateRange,
      selectedDateSetter: setSelectedDate
    });

  const handleRightBtn = () =>
    handlePickerRightButton({
      endRange: dateRange[1],
      dateRangeMode: dateRangeModeRef.current,
      dateRangeSetter: setDateRange,
      selectedDateSetter: setSelectedDate
    });

  const fetchLeadServiceGlobalInsights = useCallback(async () => {
    // Abort previous request if still pending
    if (controllerRef.current) {
      controllerRef.current.abort();
    }
    // Create a new controller for the new request
    const controller = new AbortController();
    controllerRef.current = controller;
    try {
      setIsLoadingInsights(true);
      let startDate = dateRange[0];
      let endDate = dateRange[1];
      let dateRangeMode = dateRangeModeRef.current;
      if (dateRangeMode === DATE_RANGE_MODE.RANGE) {
        if (!startDate || !endDate) {
          return;
        }
        dateRangeMode = findRangeMode({ startDate, endDate });
      }
      endDate = new Date(endDate.toDateString());
      endDate.setDate(endDate.getDate() + 1);
      if (dateRangeModeRef.current === DATE_RANGE_MODE.ALL_TIME) {
        startDate = endDate = undefined;
      }
      const params = { lsType, startDate, endDate, dateRangeMode };
      const response = await getLeadServiceGlobalInsights(
        params,
        controller.signal
      );
      controllerRef.current = null; // Clear controller after success
      setLsInsights(
        formatLeadServiceInsightsForBarChart({
          collection: response.data.data
        })
      );
    } catch (error) {
      if (error.name === "CanceledError" || error.name === "AbortError") {
        return;
      } else {
        console.error("Error fetching lead service global insights:", error);
      }
    } finally {
      setIsLoadingInsights(false);
    }
  }, [lsType, dateRange]);

  useEffect(() => {
    fetchEmailTemplates();
  }, [fetchEmailTemplates]);
  useEffect(() => {
    fetchWorkspaceUserData();
  }, []);
  useEffect(() => {
    fetchLeadServiceGlobalInsights();
  }, [fetchLeadServiceGlobalInsights]);

  return (
    <>
      {!isLoading ? (
        <div>
          <PageHeading>Lead Service</PageHeading>

          <Container fluid className="mt-4 px-0">
            <Row>
              <Col sm={12} md={3}>
                <LeadServicePanelWorkspaceConfigField
                  fieldType="number"
                  controlId="available-after-delay"
                  isLoading={loadingLeadBlock}
                  fieldLabel={t("availableAfter")}
                  fieldValue={availableAfter}
                  fieldSetter={setAvailableAfter}
                  handleOnSave={updateLeadBlockTimeFunc}
                  hasClasses
                />
              </Col>
              <Col sm={12} md={3} className="mt-sm-2 mt-md-0">
                <LeadServicePanelWorkspaceConfigField
                  fieldType="number"
                  controlId="offer-followup-max-not-reached-attempts"
                  isLoading={loadingOfferFollowupAttempts}
                  fieldLabel={t("offerFollowupMaxNotReachedAttempts")}
                  fieldValue={offerFollowupMaxNotReachedAttempts}
                  fieldSetter={setOfferFollowupMaxNotReachedAttempts}
                  handleOnSave={updateOfferFollowupAttempts}
                  hasClasses
                />
              </Col>
              <Col sm={12} md={3} className="mt-sm-2 mt-md-0">
                <LeadServicePanelWorkspaceConfigField
                  fieldType="number"
                  controlId="calling-down-time"
                  isLoading={loadingDownTime}
                  fieldLabel={t("callingDownTimeLabel")}
                  fieldValue={systemCallingDownTime}
                  fieldSetter={setSystemCallingDownTime}
                  handleOnSave={updateSystemCallingDownTime}
                />
              </Col>
              <Col sm={12} md={3} className="mt-sm-2 mt-md-0">
                <LeadServicePanelWorkspaceConfigField
                  fieldType="text"
                  fieldWidth="w-75"
                  controlId="webhook-integration"
                  isLoading={loadingDownTimeWebhook}
                  fieldLabel={t("aiLeadservice.webhookIntegrationLabel")}
                  fieldValue={systemCallingDownTimeWebhook}
                  fieldSetter={setSystemCallingDownTimeWebhook}
                  handleOnSave={updateCallingDownTimeWebhook}
                />
              </Col>
            </Row>

            {hasAccess(ACCESS_CONFIG.ONLY_SUPER_ADMIN) && (
              <Row className="mt-4">
                <Col xs={12}>
                  <LeadServicePanelDailyReportWebhook />
                </Col>
              </Row>
            )}

            {/* AI Leadservice - LEAD (Neukunden) */}
            <Row className="mt-4">
              <Col xs={12}>
                <h5 className="fw-bold mb-3">
                  {t("aiLeadservice.lead.heading")}
                </h5>
              </Col>
              <Col sm={12} md={4}>
                <LeadServicePanelWorkspaceConfigField
                  fieldType="checkbox"
                  controlId="ai-leadservice-lead-enabled"
                  isLoading={loadingAiLeadEnabled}
                  fieldLabel={t("common.enable")}
                  fieldValue={aiLeadserviceLeadEnabled}
                  fieldSetter={setAiLeadserviceLeadEnabled}
                  handleOnSave={handleSaveAiLeadserviceLeadEnabled}
                />
              </Col>
              <Col sm={12} md={4} className="mt-sm-2 mt-md-0">
                <LeadServicePanelWorkspaceConfigField
                  fieldType="text"
                  fieldWidth="w-75"
                  controlId="ai-leadservice-lead-webhook"
                  isLoading={loadingAiLeadWebhook}
                  fieldLabel={t("aiLeadservice.webhookUrlLabel")}
                  fieldValue={aiLeadserviceLeadWebhook}
                  fieldSetter={setAiLeadserviceLeadWebhook}
                  handleOnSave={handleSaveAiLeadserviceLeadWebhook}
                  disabled={!aiLeadserviceLeadEnabled}
                />
              </Col>
              <Col sm={12} md={4} className="mt-sm-2 mt-md-0">
                <LeadServicePanelWorkspaceConfigField
                  fieldType="number"
                  controlId="ai-leadservice-lead-trigger"
                  isLoading={loadingAiLeadTrigger}
                  fieldLabel={t("aiLeadservice.attemptsLabel")}
                  fieldValue={aiLeadserviceLeadTrigger}
                  fieldSetter={setAiLeadserviceLeadTrigger}
                  handleOnSave={handleSaveAiLeadserviceLeadTrigger}
                  disabled={!aiLeadserviceLeadEnabled}
                />
              </Col>
            </Row>

            {/* AI Leadservice - EMPLOYEE (Mitarbeitergewinnung) */}
            <Row className="mt-4">
              <Col xs={12}>
                <h5 className="fw-bold mb-3">
                  {t("aiLeadservice.employee.heading")}
                </h5>
              </Col>
              <Col sm={12} md={4}>
                <LeadServicePanelWorkspaceConfigField
                  fieldType="checkbox"
                  controlId="ai-leadservice-employee-enabled"
                  isLoading={loadingAiEmployeeEnabled}
                  fieldLabel={t("common.enable")}
                  fieldValue={aiLeadserviceEmployeeEnabled}
                  fieldSetter={setAiLeadserviceEmployeeEnabled}
                  handleOnSave={handleSaveAiLeadserviceEmployeeEnabled}
                />
              </Col>
              <Col sm={12} md={4} className="mt-sm-2 mt-md-0">
                <LeadServicePanelWorkspaceConfigField
                  fieldType="text"
                  fieldWidth="w-75"
                  controlId="ai-leadservice-employee-webhook"
                  isLoading={loadingAiEmployeeWebhook}
                  fieldLabel={t("aiLeadservice.webhookUrlLabel")}
                  fieldValue={aiLeadserviceEmployeeWebhook}
                  fieldSetter={setAiLeadserviceEmployeeWebhook}
                  handleOnSave={handleSaveAiLeadserviceEmployeeWebhook}
                  disabled={!aiLeadserviceEmployeeEnabled}
                />
              </Col>
              <Col sm={12} md={4} className="mt-sm-2 mt-md-0">
                <LeadServicePanelWorkspaceConfigField
                  fieldType="number"
                  controlId="ai-leadservice-employee-trigger"
                  isLoading={loadingAiEmployeeTrigger}
                  fieldLabel={t("aiLeadservice.attemptsLabel")}
                  fieldValue={aiLeadserviceEmployeeTrigger}
                  fieldSetter={setAiLeadserviceEmployeeTrigger}
                  handleOnSave={handleSaveAiLeadserviceEmployeeTrigger}
                  disabled={!aiLeadserviceEmployeeEnabled}
                />
              </Col>
            </Row>

            <LeadserviceOverview />

            <CustomCard>
              <Card.Header className="bg-white kpi-tiles-header">
                <div className="w-100 d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 fw-bold">
                    {t("aiLeadservice.trackingGlobalHeading")}
                  </h5>
                  <div className="d-flex align-items-center">
                    <div className="me-4">
                      <LeadServiceTypesFilter
                        selectedLsType={lsType}
                        lsTypeSetter={setLsType}
                      />
                    </div>
                    <div className="d-flex align-items-center">
                      {dateRangeModeRef.current !==
                        DATE_RANGE_MODE.ALL_TIME && (
                        <DatePickerLimitRange
                          mode={DATE_RANGE_MODE}
                          className="w-50"
                          placeholderText={RADIOS[radioValue - 1].name}
                          dateRangeMode={dateRangeModeRef.current}
                          maxDate={moment.now()}
                          selectedDate={selectedDate}
                          handleLeftBtn={handleLeftBtn}
                          handleRightBtn={handleRightBtn}
                          dateRange={dateRange}
                          onRangeChange={onRangeChange}
                        />
                      )}
                      <ButtonGroup>
                        {RADIOS.map((radio, idx) => (
                          <ToggleBtn
                            key={idx}
                            id={`radio-${idx}`}
                            type="radio"
                            className="kpi-toggle-button"
                            name="radio"
                            value={radio.value}
                            checked={radioValue === radio.value}
                            onChange={(e) =>
                              handleRadioSelect(e.currentTarget.value)
                            }
                          >
                            {radio.name}
                          </ToggleBtn>
                        ))}
                      </ButtonGroup>
                    </div>
                  </div>
                </div>
              </Card.Header>
              {isLoadingInsights ? (
                <Loader calcHeight={550} />
              ) : lsInisights.total ? (
                <LeadServiceInsightsBarChart data={lsInisights} />
              ) : (
                <h6 className="mb-0 fw-bold p-5 align-self-center">
                  {t("noData")}
                </h6>
              )}
            </CustomCard>

            <div className="mt-5 mb-3">
              <FormProvider {...methods}>
                <Row className="mb-3">
                  {Object.keys(APPOINTMENT_EMAIL_TEMPLATES).map((typeKey) => {
                    const type = APPOINTMENT_EMAIL_TEMPLATES[typeKey];
                    const emailData = getValues(type) || {};
                    return (
                      <Col md={12} lg={3} key={type}>
                        <CustomCard>
                          <Card.Header className="d-flex align-items-center justify-content-between">
                            <div className="fw-bold">{getTitle(type)}</div>

                            <Button
                              onClick={() => handleOpenModal(type)}
                              size="sm"
                              variant="success"
                            >
                              {t("common.edit")}
                            </Button>
                          </Card.Header>
                          <SubjectPreview className="p-2">
                            <span className="fw-bold">
                              {t("emailEdit.subject")}:
                            </span>{" "}
                            <span>
                              {emailData.subject || (
                                <em>{t("emailEdit.noSubject")}</em>
                              )}
                            </span>
                          </SubjectPreview>
                          <HorizontalLine />
                          <EmailPreview
                            className="mt-0 p-2 email-preview"
                            dangerouslySetInnerHTML={{
                              __html:
                                emailData.content ||
                                `<em>${t("emailEdit.noContent")}</em>`
                            }}
                          ></EmailPreview>
                        </CustomCard>
                      </Col>
                    );
                  })}
                </Row>
                {modalType && (
                  <EditorModal
                    className="email-content"
                    title={getTitle(modalType)}
                    quillValue={getValues(modalType)?.content || ""}
                    subjectValue={getValues(modalType)?.subject || ""}
                    onSave={handleSaveContent}
                    onCancel={handleCloseModal}
                  />
                )}
              </FormProvider>
            </div>

            <Row className="mt-5 mb-3">
              <Col md={12} lg={3}>
                <CustomCard>
                  <Card.Header className="d-flex align-items-center justify-content-between">
                    <div className="fw-bold">Leadservice {t("script")}</div>
                    <Button
                      size="sm"
                      variant="success"
                      onClick={() => onEditClick(SCRIPT_ROUTE.LS)}
                    >
                      {t("common.change")}
                    </Button>
                  </Card.Header>
                  <div className="p-3">
                    <PDFViewer
                      ariaLabel="default"
                      scriptUrl={generateScriptUrl({
                        workspace: user.workspace,
                        scriptProp: SCRIPT_WORKSPACE_PATH.LS,
                        localFileName: SCRIPT_LOCAL_FILE_NAME.LS
                      })}
                    />
                  </div>
                </CustomCard>
              </Col>
              <Col md={12} lg={3}>
                <CustomCard>
                  <Card.Header className="d-flex align-items-center justify-content-between">
                    <div className="fw-bold">
                      Leadservice<PlusSpan>+</PlusSpan> {t("script")}
                    </div>
                    <Button
                      size="sm"
                      variant="success"
                      onClick={() => onEditClick(SCRIPT_ROUTE.LS_PLUS)}
                    >
                      {t("common.change")}
                    </Button>
                  </Card.Header>
                  <div className="p-3">
                    <PDFViewer
                      ariaLabel="ls-plus"
                      scriptUrl={generateScriptUrl({
                        workspace: user.workspace,
                        scriptProp: SCRIPT_WORKSPACE_PATH.LS_PLUS,
                        localFileName: SCRIPT_LOCAL_FILE_NAME.LS_PLUS
                      })}
                    />
                  </div>
                </CustomCard>
              </Col>
              <Col md={12} lg={3}>
                <CustomCard>
                  <Card.Header className="d-flex align-items-center justify-content-between">
                    <div className="fw-bold">
                      {t("appointmentConfirm")} {t("script")}
                    </div>
                    <Button
                      size="sm"
                      variant="success"
                      onClick={() => onEditClick(SCRIPT_ROUTE.AP)}
                    >
                      {t("common.change")}
                    </Button>
                  </Card.Header>
                  <div className="p-3">
                    <PDFViewer
                      ariaLabel="ap"
                      scriptUrl={generateScriptUrl({
                        workspace: user.workspace,
                        scriptProp: SCRIPT_WORKSPACE_PATH.AP,
                        localFileName: SCRIPT_LOCAL_FILE_NAME.AP
                      })}
                    />
                  </div>
                </CustomCard>
              </Col>
              <Col md={12} lg={3}>
                <CustomCard>
                  <Card.Header className="d-flex align-items-center justify-content-between">
                    <div className="fw-bold">
                      {t("filteredLead")} {t("script")}
                    </div>
                    <Button
                      size="sm"
                      variant="success"
                      onClick={() => onEditClick(SCRIPT_ROUTE.RECLAIM_LEADS)}
                    >
                      {t("common.change")}
                    </Button>
                  </Card.Header>
                  <div className="p-3">
                    <PDFViewer
                      ariaLabel="ap"
                      scriptUrl={generateScriptUrl({
                        workspace: user.workspace,
                        scriptProp: SCRIPT_WORKSPACE_PATH.RECLAIM_LEADS,
                        localFileName: SCRIPT_LOCAL_FILE_NAME.RECLAIM_LEADS
                      })}
                    />
                  </div>
                </CustomCard>
              </Col>
            </Row>
            <Row className="mt-5 mb-3">
              <Col md={12} lg={3}>
                <CustomCard>
                  <Card.Header className="d-flex align-items-center justify-content-between">
                    <div className="fw-bold">
                      {t("offerFollowup")} {t("script")}
                    </div>
                    <Button
                      size="sm"
                      variant="success"
                      onClick={() => onEditClick(SCRIPT_ROUTE.FOLLOWUP)}
                    >
                      {t("common.change")}
                    </Button>
                  </Card.Header>
                  <div className="p-3">
                    <PDFViewer
                      ariaLabel="ap"
                      scriptUrl={generateScriptUrl({
                        workspace: user.workspace,
                        scriptProp: SCRIPT_WORKSPACE_PATH.FOLLOWUP,
                        localFileName: SCRIPT_LOCAL_FILE_NAME.FOLLOWUP
                      })}
                    />
                  </div>
                </CustomCard>
              </Col>
            </Row>
            <UploadScriptDialog
              title={scriptTitle}
              show={showScriptDialog}
              toggle={toggleUploadDialog}
            />
          </Container>
        </div>
      ) : (
        <Loader />
      )}
    </>
  );
};
export default Leadservice;
